// src/pages/customer/CustomerProfile.js
// Página de perfil del cliente que muestra todos sus datos y lista todas sus mascotas

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Table, Modal, Badge, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import { useAuth } from '../../context/AuthContext';
import { getCustomerById, updateCustomer } from '../../services/customerApi';
import { getPetsByClientId, createPet, updatePet } from '../../services/petApi';
import { createAppointment, getAppointmentsByPetWithServices } from '../../services/appointmentApi';
import { getLoyaltyCardsByClientId } from '../../services/loyaltyCardApi';
import AppointmentForm from '../appointment/AppointmentForm';
import PetForm from '../pet/PetForm';
import CustomerForm from './CustomerForm';
import AlertModal from '../../components/AlertModal';
import useAlertModal from '../../hooks/useAlertModal';

const CustomerProfile = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Estados
  const [customer, setCustomer] = useState(null);
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [petsLoading, setPetsLoading] = useState(true);
  const { showAlert, showAlertModal, alertMessage, alertType, hideAlert, getErrorMessage } = useAlertModal();
  
  // Estados para el modal de agendamiento
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Estados para el modal de creación de mascota
  const [showPetModal, setShowPetModal] = useState(false);
  const [petModalLoading, setPetModalLoading] = useState(false);
  
  // Estados para el modal de edición de mascota
  const [showEditPetModal, setShowEditPetModal] = useState(false);
  const [selectedPetForEdit, setSelectedPetForEdit] = useState(null);
  const [editPetModalLoading, setEditPetModalLoading] = useState(false);
  
  // Estados para el modal de edición de cliente
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [editCustomerModalLoading, setEditCustomerModalLoading] = useState(false);
  
  // Estados para tarjetas de fidelización
  const [loyaltyCardData, setLoyaltyCardData] = useState(null);
  const [loyaltyCardsLoading, setLoyaltyCardsLoading] = useState(false);

  // Estados para modal de historial de citas por mascota
  const [showHistorialModal, setShowHistorialModal] = useState(false);
  const [selectedPetForHistorial, setSelectedPetForHistorial] = useState(null);
  const [historialData, setHistorialData] = useState([]);
  const [historialLoading, setHistorialLoading] = useState(false);
  // Cita seleccionada para ver detalle completo (desde el historial)
  const [selectedAppointmentDetail, setSelectedAppointmentDetail] = useState(null);

  // Cargar datos del cliente
  useEffect(() => {
    const loadCustomer = async () => {
      if (!id) {
        showAlert('ID de cliente no proporcionado', 'error');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        const { ok, status, data } = await getCustomerById(parseInt(id));
        
        if (ok && (status === 200 || data?.code === 200)) {
          const customerData = data.data || data.message || data;
          setCustomer(customerData);
        } else {
          const msg = getErrorMessage({ data, status }) || 'Error cargando cliente';
          showAlert(msg, 'error');
        }
      } catch (error) {
        console.error('Error cargando cliente:', error);
        showAlert('Error inesperado al cargar cliente', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadCustomer();
  }, [id]);

  // Función para cargar mascotas (reutilizable)
  const loadPets = useCallback(async () => {
    if (!id) {
      setPetsLoading(false);
      return;
    }

    try {
      setPetsLoading(true);
      
      const { ok, status, data } = await getPetsByClientId(parseInt(id));
      
      if (ok && (status === 200 || data?.code === 200)) {
        const petsData = data.data || data.message || [];
        const petsList = Array.isArray(petsData) ? petsData : [];
        setPets(petsList);
      } else {
        const msg = getErrorMessage({ data, status }) || 'Error cargando mascotas';
        showAlert(msg, 'error');
      }
    } catch (error) {
      console.error('Error cargando mascotas:', error);
      showAlert('Error inesperado al cargar mascotas', 'error');
    } finally {
      setPetsLoading(false);
    }
  }, [id]);

  // Cargar mascotas del cliente
  useEffect(() => {
    loadPets();
  }, [loadPets]);

  // Cargar tarjetas de fidelización del cliente
  useEffect(() => {
    const loadLoyaltyCards = async () => {
      if (!id || !customer?.id_client) {
        return;
      }

      try {
        setLoyaltyCardsLoading(true);
        const { ok, status, data } = await getLoyaltyCardsByClientId(customer.id_client);
        
        if (ok && (status === 200 || data?.code === 200)) {
          // La respuesta puede venir en data.data o directamente en data
          const cardData = data.data || data;
          setLoyaltyCardData(cardData);
        }
      } catch (error) {
        console.error('Error cargando tarjetas de fidelización:', error);
        setLoyaltyCardData(null);
      } finally {
        setLoyaltyCardsLoading(false);
      }
    };

    if (customer?.id_client) {
      loadLoyaltyCards();
    }
  }, [id, customer?.id_client]);

  // Calcular edad de la mascota
  const calculateAge = (birthdate) => {
    if (!birthdate) return 'N/A';
    const birth = new Date(birthdate);
    const today = new Date();
    const ageInMonths = Math.floor((today - birth) / (1000 * 60 * 60 * 24 * 30.44));
    
    if (ageInMonths < 12) {
      return `${ageInMonths} meses`;
    } else {
      const years = Math.floor(ageInMonths / 12);
      const months = ageInMonths % 12;
      return months > 0 ? `${years} años ${months} meses` : `${years} años`;
    }
  };

  // Obtener nombre de especie
  const getSpeciesName = (idSpecie) => {
    const species = {
      1: 'Perro',
      2: 'Gato',
      3: 'Ave',
      4: 'Conejo',
      5: 'Hamster',
      6: 'Otro'
    };
    return species[idSpecie] || 'Desconocido';
  };

  // Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Obtener cantidad de tarjetas completadas
  const getCardsCompleted = (cardData) => {
    if (!cardData) return 0;
    
    // El campo exacto es cards_completed (minúsculas)
    return cardData.cards_completed !== undefined ? cardData.cards_completed :0;
  };

  // Calcular ranking de fidelidad basado en cantidad de tarjetas (lineal: 1 tarjeta = 1 estrella, máximo 5)
  const calculateLoyaltyRating = (cardsCount) => {
    if (cardsCount === 0) return 0;
    // 1 tarjeta = 1 estrella, 5 tarjetas = 5 estrellas, máximo 5
    return Math.min(cardsCount, 5);
  };

  // Determinar el nivel del cliente basado en tarjetas completadas
  const getCustomerLevel = (cardsCount) => {
    if (cardsCount === 0) return null;
    if (cardsCount <= 2) return { name: 'Bronce', variant: 'bronze', customColor: '#CD7F32' };
    if (cardsCount <= 4) return { name: 'Plata', variant: 'light' };
    return { name: 'Oro', variant: 'warning' }; // 5 o más tarjetas
  };

  // Componente de estrellas de fidelidad
  const LoyaltyStars = ({ rating }) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <i
          key={i}
          className={`fas fa-star ${i <= rating ? 'text-warning' : 'text-muted'}`}
          style={{ fontSize: '1.2rem' }}
        />
      );
    }
    return <span>{stars}</span>;
  };

  // Abrir modal de agendamiento de cita
  const handleOpenAppointmentModal = (pet) => {
    setSelectedPet(pet);
    // Establecer la fecha de hoy por defecto
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];
    setSelectedDate(dateStr);
    setShowAppointmentModal(true);
  };

  // Cerrar modal de agendamiento
  const handleCloseAppointmentModal = () => {
    setShowAppointmentModal(false);
    setSelectedPet(null);
    setSelectedDate(null);
  };

  // Manejar creación de cita
  const handleCreateAppointment = async (appointmentData) => {
    try {
      setModalLoading(true);
      
      const { ok, status, data } = await createAppointment(appointmentData);
      
      if (ok && (status === 201 || data?.code === 201)) {
        showAlert(data?.message || 'Cita agendada exitosamente', 'success');
        setShowAppointmentModal(false);
        setSelectedPet(null);
        setSelectedDate(null);
        // Opcional: recargar datos si es necesario
      } else {
        const msg = getErrorMessage({ data, status }) || 'Error agendando cita';
        showAlert(msg, 'error');
      }
    } catch (error) {
      console.error('Error agendando cita:', error);
      showAlert('Error inesperado al agendar cita', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  // Formatear fecha para mostrar en el modal
  const formatSelectedDate = () => {
    if (!selectedDate) return '';
    
    const date = new Date(selectedDate);
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    
    return date.toLocaleDateString('es-ES', options);
  };

  // Abrir modal de creación de mascota
  const handleOpenPetModal = () => {
    setShowPetModal(true);
  };

  // Cerrar modal de creación de mascota
  const handleClosePetModal = () => {
    setShowPetModal(false);
  };

  // Manejar creación de mascota
  const handleCreatePet = async (petData) => {
    try {
      setPetModalLoading(true);
      
      // Agregar el ID del cliente
      const petDataWithClient = {
        ...petData,
        id_client: customer.id_client
      };
      
      const { ok, status, data } = await createPet(petDataWithClient);
      
      if (ok && (status === 201 || data?.code === 201)) {
        showAlert(data?.message || 'Mascota creada exitosamente', 'success');
        setShowPetModal(false);
        // Recargar la lista de mascotas
        loadPets();
      } else {
        const msg = getErrorMessage({ data, status }) || 'Error creando mascota';
        showAlert(msg, 'error');
      }
    } catch (error) {
      console.error('Error creando mascota:', error);
      showAlert('Error inesperado al crear mascota', 'error');
    } finally {
      setPetModalLoading(false);
    }
  };

  // Abrir modal de historial de citas de la mascota
  const handleOpenHistorial = async (pet) => {
    setSelectedPetForHistorial(pet);
    setShowHistorialModal(true);
    setHistorialData([]);
    setHistorialLoading(true);
    try {
      const { ok, status, data } = await getAppointmentsByPetWithServices(pet.id_pet);
      if (ok && (status === 200 || data?.code === 200)) {
        const list = data?.data ?? data?.message ?? [];
        setHistorialData(Array.isArray(list) ? list : []);
      } else {
        setHistorialData([]);
      }
    } catch (error) {
      console.error('Error cargando historial:', error);
      setHistorialData([]);
    } finally {
      setHistorialLoading(false);
    }
  };

  const handleCloseHistorialModal = () => {
    setShowHistorialModal(false);
    setSelectedPetForHistorial(null);
    setHistorialData([]);
    setSelectedAppointmentDetail(null);
  };

  // Abrir modal de edición de mascota
  const handleOpenEditPetModal = (pet) => {
    setSelectedPetForEdit(pet);
    setShowEditPetModal(true);
  };

  // Cerrar modal de edición de mascota
  const handleCloseEditPetModal = () => {
    setShowEditPetModal(false);
    setSelectedPetForEdit(null);
  };

  // Manejar actualización de mascota
  const handleUpdatePet = async (petData) => {
    try {
      setEditPetModalLoading(true);
      
      const { ok, status, data } = await updatePet(selectedPetForEdit.id_pet, petData);
      
      if (ok && (status === 200 || data?.code === 200)) {
        showAlert(data?.message || 'Mascota actualizada exitosamente', 'success');
        setShowEditPetModal(false);
        setSelectedPetForEdit(null);
        // Recargar la lista de mascotas
        loadPets();
      } else {
        const msg = getErrorMessage({ data, status }) || 'Error actualizando mascota';
        showAlert(msg, 'error');
      }
    } catch (error) {
      console.error('Error actualizando mascota:', error);
      showAlert('Error inesperado al actualizar mascota', 'error');
    } finally {
      setEditPetModalLoading(false);
    }
  };

  // Abrir modal de edición de cliente
  const handleOpenEditCustomerModal = () => {
    setShowEditCustomerModal(true);
  };

  // Cerrar modal de edición de cliente
  const handleCloseEditCustomerModal = () => {
    setShowEditCustomerModal(false);
  };

  // Manejar actualización de cliente
  const handleUpdateCustomer = async (customerData) => {
    try {
      setEditCustomerModalLoading(true);
      
      const { ok, status, data } = await updateCustomer(customer.id_client, customerData);
      
      if (ok && (status === 200 || data?.code === 200)) {
        showAlert(data?.message || 'Cliente actualizado exitosamente', 'success');
        setShowEditCustomerModal(false);
        // Recargar los datos del cliente
        const loadCustomer = async () => {
          try {
            setLoading(true);
            const { ok, status, data } = await getCustomerById(parseInt(id));
            
            if (ok && (status === 200 || data?.code === 200)) {
              const customerData = data.data || data.message || data;
              setCustomer(customerData);
            }
          } catch (error) {
            console.error('Error cargando cliente:', error);
          } finally {
            setLoading(false);
          }
        };
        loadCustomer();
      } else {
        const msg = getErrorMessage({ data, status }) || 'Error actualizando cliente';
        showAlert(msg, 'error');
      }
    } catch (error) {
      console.error('Error actualizando cliente:', error);
      showAlert('Error inesperado al actualizar cliente', 'error');
    } finally {
      setEditCustomerModalLoading(false);
    }
  };

  return (
    <>
      <NavContainer />
      <OffCanvasNav />

      <Container fluid className="mt-3">
        <Row>
          <Col>
            {/* Botón de regreso */}
            <Button 
              variant="outline-secondary" 
              className="mb-3"
              onClick={() => navigate('/gestion-clientes')}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Volver a Gestión de Clientes
            </Button>

            {/* Información del Cliente */}
            <Card className="mb-4">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Perfil del Cliente</h4>
                {customer && (
                  <Button 
                    variant="primary"
                    onClick={handleOpenEditCustomerModal}
                  >
                    <i className="fas fa-edit me-2"></i>
                    Editar Cliente
                  </Button>
                )}
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="d-flex justify-content-center py-4">
                    <Spinner animation="border" />
                  </div>
                ) : customer ? (
                  <Row>
                    <Col md={6}>
                      <h5 className="mb-3">Información Personal</h5>
                      <Table borderless>
                        <tbody>
                          <tr>
                            <td><strong>ID Cliente:</strong></td>
                            <td>{customer.id_client || 'N/A'}</td>
                          </tr>
                          <tr>
                            <td><strong>Nombre:</strong></td>
                            <td>{customer.name || 'N/A'}</td>
                          </tr>
                          <tr>
                            <td><strong>Primer Apellido:</strong></td>
                            <td>{customer.surname || 'N/A'}</td>
                          </tr>
                          <tr>
                            <td><strong>Segundo Apellido:</strong></td>
                            <td>{customer.lastname || 'N/A'}</td>
                          </tr>
                          <tr>
                            <td><strong>Apellido Adicional:</strong></td>
                            <td>{customer.second_surname || 'N/A'}</td>
                          </tr>
                          <tr>
                            <td><strong>Nombre Completo:</strong></td>
                            <td>
                              {`${customer.name || ''} ${customer.surname || ''} ${customer.lastname || ''} ${customer.second_surname || ''}`.trim() || 'N/A'}
                            </td>
                          </tr>
                          <tr>
                            <td><strong>Documento:</strong></td>
                            <td>{customer.document || customer.person?.document || 'N/A'}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                    <Col md={6}>
                      <h5 className="mb-3">Información de Contacto</h5>
                      <Table borderless>
                        <tbody>
                          <tr>
                            <td><strong>Email:</strong></td>
                            <td>{customer.email || customer.person?.email || 'N/A'}</td>
                          </tr>
                          <tr>
                            <td><strong>WhatsApp:</strong></td>
                            <td>{customer.phone_whatsapp || customer.person?.phone_whatsapp || 'N/A'}</td>
                          </tr>
                          <tr>
                            <td><strong>Teléfono 2:</strong></td>
                            <td>{customer.phone_2 || 'N/A'}</td>
                          </tr>
                          <tr>
                            <td><strong>Marketing:</strong></td>
                            <td>
                              <span className={`badge ${customer.marketing_opt_in ? 'bg-success' : 'bg-secondary'}`}>
                                {customer.marketing_opt_in ? 'Sí' : 'No'}
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td><strong>Cliente desde:</strong></td>
                            <td>
                              {customer.created_at || customer.Created_AT || customer.created_AT || customer.Created_at 
                                ? formatDate(customer.created_at || customer.Created_AT || customer.created_AT || customer.Created_at)
                                : 'N/A'}
                            </td>
                          </tr>
                          <tr>
                            <td><strong>Tarjetas de Fidelización:</strong></td>
                            <td>
                              {loyaltyCardsLoading ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <>
                                  {getCardsCompleted(loyaltyCardData)} tarjeta{getCardsCompleted(loyaltyCardData) !== 1 ? 's' : ''} completada{getCardsCompleted(loyaltyCardData) !== 1 ? 's' : ''}
                                </>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td><strong>Nivel de Fidelidad:</strong></td>
                            <td>
                              {loyaltyCardsLoading ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <>
                                  <LoyaltyStars rating={calculateLoyaltyRating(getCardsCompleted(loyaltyCardData))} />
                                  {getCustomerLevel(getCardsCompleted(loyaltyCardData)) && (() => {
                                    const level = getCustomerLevel(getCardsCompleted(loyaltyCardData));
                                    if (level.customColor) {
                                      // Para Bronce, usar span con clase badge y estilo personalizado
                                      return (
                                        <span className="ms-2 d-inline-flex align-items-center">
                                          <i className="fas fa-medal me-1" style={{ color: level.customColor }}></i>
                                          <span 
                                            style={{ 
                                              fontSize: '0.9rem', 
                                              padding: '0.4rem 0.8rem',
                                              backgroundColor: level.customColor,
                                              color: '#fff',
                                              border: 'none',
                                              borderRadius: '0.375rem',
                                              display: 'inline-block',
                                              fontWeight: '700',
                                              lineHeight: '1',
                                              textAlign: 'center',
                                              textDecoration: 'none',
                                              verticalAlign: 'baseline',
                                              whiteSpace: 'nowrap'
                                            }}
                                          >
                                            {level.name}
                                          </span>
                                        </span>
                                      );
                                    }
                                    // Para Plata y Oro, usar Badge con variant normal
                                    return (
                                      <span className="ms-2 d-inline-flex align-items-center">
                                        <i 
                                          className="fas fa-medal me-1" 
                                          style={{ 
                                            color: level.name === 'Plata' ? '#6c757d' : '#ffc107' 
                                          }}
                                        ></i>
                                        <Badge 
                                          bg={level.variant}
                                          style={{ 
                                            fontSize: '0.9rem', 
                                            padding: '0.4rem 0.8rem',
                                            color: level.name === 'Plata' ? '#000' : undefined
                                          }}
                                        >
                                          {level.name}
                                        </Badge>
                                      </span>
                                    );
                                  })()}
                                </>
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                ) : (
                  <Alert variant="warning">
                    No se encontró información del cliente
                  </Alert>
                )}
              </Card.Body>
            </Card>

            {/* Lista de Mascotas */}
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Mascotas del Cliente</h4>
                {customer && (
                  <Button 
                    variant="success"
                    onClick={handleOpenPetModal}
                  >
                    <i className="fas fa-plus me-2"></i>
                    Nueva Mascota
                  </Button>
                )}
              </Card.Header>
              <Card.Body>
                {petsLoading ? (
                  <div className="d-flex justify-content-center py-4">
                    <Spinner animation="border" />
                  </div>
                ) : pets.length === 0 ? (
                  <Alert variant="info">
                    <i className="fas fa-info-circle me-2"></i>
                    Este cliente no tiene mascotas registradas
                  </Alert>
                ) : (
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Imagen</th>
                        <th>Nombre</th>
                        <th>Especie</th>
                        <th>Raza</th>
                        <th>Peso (kg)</th>
                        <th>Fecha de Nacimiento</th>
                        <th>Edad</th>
                        <th>Notas</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pets.map((pet) => (
                        <tr key={pet.id_pet}>
                          <td>{pet.id_pet}</td>
                          <td>
                            {pet.profile_image_url ? (
                              <img
                                src={pet.profile_image_url}
                                alt={pet.name || 'Mascota'}
                                style={{
                                  width: '50px',
                                  height: '50px',
                                  objectFit: 'cover',
                                  borderRadius: '50%',
                                  border: '2px solid #dee2e6'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              style={{
                                display: pet.profile_image_url ? 'none' : 'flex',
                                width: '50px',
                                height: '50px',
                                borderRadius: '50%',
                                backgroundColor: '#f0f0f0',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px solid #dee2e6'
                              }}
                            >
                              <i className="fas fa-paw text-muted"></i>
                            </div>
                          </td>
                          <td><strong>{pet.name || 'N/A'}</strong></td>
                          <td>{getSpeciesName(pet.id_specie)}</td>
                          <td>{pet.breed || 'N/A'}</td>
                          <td>{pet.weight_kg || 'N/A'}</td>
                          <td>{formatDate(pet.birthdate)}</td>
                          <td>{calculateAge(pet.birthdate)}</td>
                          <td>
                            {pet.notes ? (
                              <span 
                                title={pet.notes}
                                style={{ 
                                  display: 'block',
                                  maxWidth: '200px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {pet.notes}
                              </span>
                            ) : (
                              <span className="text-muted">Sin notas</span>
                            )}
                          </td>
                          <td>
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              className="me-2"
                              onClick={() => handleOpenEditPetModal(pet)}
                              title="Editar mascota"
                            >
                              <i className="fas fa-edit me-1"></i>
                              Editar
                            </Button>
                            <Button 
                              variant="outline-success" 
                              size="sm"
                              className="me-2"
                              onClick={() => handleOpenAppointmentModal(pet)}
                              title="Agendar cita para esta mascota"
                            >
                              <i className="fas fa-calendar-alt me-1"></i>
                              Cita
                            </Button>
                            <Button 
                              variant="outline-info" 
                              size="sm"
                              onClick={() => handleOpenHistorial(pet)}
                              title="Ver historial de citas"
                            >
                              <i className="fas fa-history me-1"></i>
                              Historial
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>

            {/* Modal de historial de citas por mascota */}
            <Modal show={showHistorialModal} onHide={handleCloseHistorialModal} size="lg">
              <Modal.Header closeButton>
                <Modal.Title>
                  <i className="fas fa-history me-2"></i>
                  Historial de citas — {selectedPetForHistorial?.name || 'Mascota'}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {historialLoading ? (
                  <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" />
                  </div>
                ) : historialData.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="fas fa-inbox fa-2x mb-3"></i>
                    <p className="mb-0">No hay citas registradas para esta mascota.</p>
                  </div>
                ) : (
                  <div className="historial-list">
                    {historialData.map((apt, idx) => (
                      <Card
                        key={apt.id_appointment || idx}
                        className="mb-3 shadow-sm"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedAppointmentDetail(apt)}
                      >
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                            <div>
                              <strong>
                                {apt.start_at
                                  ? new Date(apt.start_at).toLocaleString('es-ES', {
                                      dateStyle: 'long',
                                      timeStyle: 'short'
                                    })
                                  : '—'}
                              </strong>
                              <Badge bg={apt.status_name === 'COMPLETADO' ? 'success' : apt.status_name === 'CANCELADO' ? 'danger' : 'secondary'} className="ms-2">
                                {apt.status_name || apt.id_status}
                              </Badge>
                            </div>
                            {apt.duration_min && (
                              <span className="text-muted">{apt.duration_min} min</span>
                            )}
                          </div>
                          {apt.services && apt.services.length > 0 && (
                            <div className="mb-2">
                              <small className="text-muted d-block mb-1">Servicios:</small>
                              <ul className="list-unstyled mb-0 small">
                                {apt.services.map((srv, i) => (
                                  <li key={srv.id_appointment_detail || i}>
                                    {srv.item_description || 'Servicio'} — Cant: {srv.quantity || 1}{' '}
                                    {srv.unit_price != null && (
                                      <>· ${(parseFloat(srv.unit_price) || 0).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {apt.informed_consent?.url_pdf_firebase && (
                            <div onClick={(e) => e.stopPropagation()}>
                              <a
                                href={apt.informed_consent.url_pdf_firebase}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-info btn-sm"
                              >
                                <i className="fas fa-file-pdf me-1"></i>
                                Ver consentimiento informado
                              </a>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseHistorialModal}>
                  Cerrar
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Modal de detalle de cita (desde historial) */}
            <Modal
              show={!!selectedAppointmentDetail}
              onHide={() => setSelectedAppointmentDetail(null)}
              size="lg"
              scrollable
            >
              {selectedAppointmentDetail && (
                <>
                  <Modal.Header closeButton>
                    <Modal.Title>
                      <i className="fas fa-calendar-check me-2"></i>
                      Detalle de la cita #{selectedAppointmentDetail.id_appointment}
                    </Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    {/* Datos de la cita */}
                    <h6 className="mb-2 text-primary">Datos de la cita</h6>
                    <Table size="sm" bordered className="mb-4">
                      <tbody>
                        <tr><td><strong>ID Cita</strong></td><td>{selectedAppointmentDetail.id_appointment}</td></tr>
                        <tr><td><strong>Mascota</strong></td><td>{selectedAppointmentDetail.pet_name || 'N/A'}</td></tr>
                        <tr><td><strong>Cliente</strong></td><td>{selectedAppointmentDetail.client_name || 'N/A'}</td></tr>
                        <tr><td><strong>Teléfono</strong></td><td>{selectedAppointmentDetail.phone || 'N/A'}</td></tr>
                        <tr>
                          <td><strong>Fecha y hora</strong></td>
                          <td>
                            {selectedAppointmentDetail.start_at
                              ? new Date(selectedAppointmentDetail.start_at).toLocaleString('es-ES', { dateStyle: 'long', timeStyle: 'short' })
                              : 'N/A'}
                          </td>
                        </tr>
                        <tr><td><strong>Duración</strong></td><td>{selectedAppointmentDetail.duration_min != null ? `${selectedAppointmentDetail.duration_min} min` : 'N/A'}</td></tr>
                        <tr>
                          <td><strong>Estado</strong></td>
                          <td>
                            <Badge bg={selectedAppointmentDetail.status_name === 'COMPLETADO' ? 'success' : selectedAppointmentDetail.status_name === 'CANCELADO' ? 'danger' : 'secondary'}>
                              {selectedAppointmentDetail.status_name || selectedAppointmentDetail.id_status}
                            </Badge>
                          </td>
                        </tr>
                        {selectedAppointmentDetail.description && (
                          <tr><td><strong>Servicio principal</strong></td><td>{selectedAppointmentDetail.description}</td></tr>
                        )}
                        <tr>
                          <td><strong>Creado</strong></td>
                          <td>
                            {selectedAppointmentDetail.created_at
                              ? new Date(selectedAppointmentDetail.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })
                              : 'N/A'}
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Actualizado</strong></td>
                          <td>
                            {selectedAppointmentDetail.updated_at
                              ? new Date(selectedAppointmentDetail.updated_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })
                              : 'N/A'}
                          </td>
                        </tr>
                        {selectedAppointmentDetail.create_by_person && (
                          <tr><td><strong>Creado por</strong></td><td>{selectedAppointmentDetail.create_by_person}</td></tr>
                        )}
                      </tbody>
                    </Table>

                    {/* Fotos antes/después */}
                    {(selectedAppointmentDetail.url_image_before || selectedAppointmentDetail.url_image_after) && (
                      <>
                        <h6 className="mb-2 text-primary">Fotos</h6>
                        <Row className="mb-4">
                          {selectedAppointmentDetail.url_image_before && (
                            <Col md={6}>
                              <small className="text-muted d-block mb-1">Antes</small>
                              <a href={selectedAppointmentDetail.url_image_before} target="_blank" rel="noopener noreferrer">
                                <img
                                  src={selectedAppointmentDetail.url_image_before}
                                  alt="Antes"
                                  style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: 8, border: '1px solid #dee2e6' }}
                                />
                              </a>
                            </Col>
                          )}
                          {selectedAppointmentDetail.url_image_after && (
                            <Col md={6}>
                              <small className="text-muted d-block mb-1">Después</small>
                              <a href={selectedAppointmentDetail.url_image_after} target="_blank" rel="noopener noreferrer">
                                <img
                                  src={selectedAppointmentDetail.url_image_after}
                                  alt="Después"
                                  style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: 8, border: '1px solid #dee2e6' }}
                                />
                              </a>
                            </Col>
                          )}
                        </Row>
                      </>
                    )}

                    {/* Servicios */}
                    {selectedAppointmentDetail.services && selectedAppointmentDetail.services.length > 0 && (
                      <>
                        <h6 className="mb-2 text-primary">Servicios realizados</h6>
                        <Table size="sm" bordered className="mb-4">
                          <thead>
                            <tr>
                              <th>Servicio</th>
                              <th>Cant.</th>
                              <th>P. unit.</th>
                              <th>Subtotal</th>
                              {selectedAppointmentDetail.services.some(s => s.item_sku) && <th>SKU</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {selectedAppointmentDetail.services.map((srv, i) => (
                              <tr key={srv.id_appointment_detail || i}>
                                <td>{srv.item_description || '—'}</td>
                                <td>{srv.quantity ?? '—'}</td>
                                <td>{srv.unit_price != null ? `$${(parseFloat(srv.unit_price)).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : '—'}</td>
                                <td>{srv.subtotal != null ? `$${(parseFloat(srv.subtotal)).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : '—'}</td>
                                {selectedAppointmentDetail.services.some(s => s.item_sku) && <td>{srv.item_sku || '—'}</td>}
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </>
                    )}

                    {/* Consentimiento informado */}
                    {selectedAppointmentDetail.informed_consent && (
                      <>
                        <h6 className="mb-2 text-primary">Consentimiento informado</h6>
                        <Table size="sm" bordered className="mb-3">
                          <tbody>
                            {selectedAppointmentDetail.informed_consent.url_pdf_firebase && (
                              <tr>
                                <td><strong>PDF</strong></td>
                                <td>
                                  <a
                                    href={selectedAppointmentDetail.informed_consent.url_pdf_firebase}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-outline-info btn-sm"
                                  >
                                    <i className="fas fa-file-pdf me-1"></i>
                                    Ver / Descargar consentimiento
                                  </a>
                                </td>
                              </tr>
                            )}
                            {selectedAppointmentDetail.informed_consent.physical_condition != null && selectedAppointmentDetail.informed_consent.physical_condition !== '' && (
                              <tr><td><strong>Estado físico</strong></td><td>{selectedAppointmentDetail.informed_consent.physical_condition}</td></tr>
                            )}
                            {selectedAppointmentDetail.informed_consent.behavior_notes != null && selectedAppointmentDetail.informed_consent.behavior_notes !== '' && (
                              <tr><td><strong>Comportamiento</strong></td><td>{selectedAppointmentDetail.informed_consent.behavior_notes}</td></tr>
                            )}
                            {selectedAppointmentDetail.informed_consent.last_vaccination_date && (
                              <tr><td><strong>Última vacunación</strong></td><td>{formatDate(selectedAppointmentDetail.informed_consent.last_vaccination_date)}</td></tr>
                            )}
                            {selectedAppointmentDetail.informed_consent.additional_notes != null && selectedAppointmentDetail.informed_consent.additional_notes !== '' && (
                              <tr><td><strong>Notas adicionales</strong></td><td>{selectedAppointmentDetail.informed_consent.additional_notes}</td></tr>
                            )}
                            {selectedAppointmentDetail.informed_consent.signed_by_person && (
                              <tr><td><strong>Firmado por</strong></td><td>{selectedAppointmentDetail.informed_consent.signed_by_person}</td></tr>
                            )}
                          </tbody>
                        </Table>
                      </>
                    )}
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="secondary" onClick={() => setSelectedAppointmentDetail(null)}>
                      Cerrar
                    </Button>
                  </Modal.Footer>
                </>
              )}
            </Modal>

            {/* Modal de agendamiento de cita */}
            <Modal show={showAppointmentModal} onHide={handleCloseAppointmentModal} size="lg">
              <Modal.Header closeButton>
                <Modal.Title>
                  Agendar Cita - {selectedPet ? selectedPet.name : ''} - {formatSelectedDate()}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {selectedPet && (
                  <AppointmentForm
                    onSubmit={handleCreateAppointment}
                    loading={modalLoading}
                    selectedDate={selectedDate}
                    initialData={{
                      id_pet: selectedPet.id_pet,
                      pet: selectedPet // Pasar el objeto completo para pre-seleccionar
                    }}
                    submitText="Agendar Cita"
                    onAlert={showAlert}
                  />
                )}
              </Modal.Body>
            </Modal>

            {/* Modal de creación de mascota */}
            <Modal show={showPetModal} onHide={handleClosePetModal} size="lg">
              <Modal.Header closeButton>
                <Modal.Title>
                  Crear Mascota para {customer ? `${customer.name} ${customer.surname}` : ''}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {customer && (
                  <PetForm
                    onSubmit={handleCreatePet}
                    loading={petModalLoading}
                    initialData={{ id_client: customer.id_client }}
                    submitText="Crear Mascota"
                  />
                )}
              </Modal.Body>
            </Modal>

            {/* Modal de edición de mascota */}
            <Modal show={showEditPetModal} onHide={handleCloseEditPetModal} size="lg">
              <Modal.Header closeButton>
                <Modal.Title>
                  Editar Mascota - {selectedPetForEdit ? selectedPetForEdit.name : ''}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {selectedPetForEdit && (
                  <PetForm
                    onSubmit={handleUpdatePet}
                    loading={editPetModalLoading}
                    initialData={selectedPetForEdit}
                    submitText="Actualizar Mascota"
                  />
                )}
              </Modal.Body>
            </Modal>

            {/* Modal de edición de cliente */}
            <Modal show={showEditCustomerModal} onHide={handleCloseEditCustomerModal} size="lg">
              <Modal.Header closeButton>
                <Modal.Title>
                  Editar Cliente - {customer ? `${customer.name} ${customer.surname}` : ''}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {customer && (
                  <CustomerForm
                    onSubmit={handleUpdateCustomer}
                    loading={editCustomerModalLoading}
                    initialData={customer}
                    submitText="Actualizar Cliente"
                  />
                )}
              </Modal.Body>
            </Modal>
          </Col>
        </Row>
        {/* Modal de Alerta Temporal */}
        <AlertModal
          show={showAlertModal}
          onHide={hideAlert}
          message={alertMessage}
          type={alertType}
        />
      </Container>
    </>
  );
};

export default CustomerProfile;
